import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';

const ToastContext = createContext(null);
const AlertContext = createContext(null);
const ConfirmContext = createContext(null);

// Module-level imperative reference for use outside React components
let _imperativeAlert = null;
export const imperativeAlert = (msg) => {
  if (_imperativeAlert) {
    _imperativeAlert(msg);
  } else {
    window.alert(msg);
  }
};

export const ToastProvider = ({ children }) => {
  const [message, setMessage] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmState, setConfirmState] = useState(null); // { message, resolve }
  const resolveRef = useRef(null);

  const showToast = useCallback((msg) => {
    setMessage(msg);
  }, []);

  const showAlert = useCallback((msg) => {
    setAlertMessage(msg);
  }, []);

  const showConfirm = useCallback((msg) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setConfirmState({ message: msg });
    });
  }, []);

  // Register the imperative reference
  useEffect(() => {
    _imperativeAlert = showAlert;
    return () => { _imperativeAlert = null; };
  }, [showAlert]);

  const dismiss = useCallback(() => setMessage(null), []);
  const dismissAlert = useCallback(() => setAlertMessage(null), []);
  const handleConfirm = useCallback((result) => {
    setConfirmState(null);
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
  }, []);

  return (
    <ToastContext.Provider value={showToast}>
      <AlertContext.Provider value={showAlert}>
        <ConfirmContext.Provider value={showConfirm}>
          {children}
          {message && createPortal(
            <div className="global-toast">
              <span className="global-toast-message">{message}</span>
              <button className="global-toast-close" onClick={dismiss}>
                <FaTimes />
              </button>
            </div>,
            document.body
          )}
          {alertMessage && createPortal(
            <div className="global-alert-overlay" onClick={dismissAlert}>
              <div className="global-alert-modal" onClick={e => e.stopPropagation()}>
                <p className="global-alert-message">{alertMessage}</p>
                <div className="global-alert-footer">
                  <button className="global-alert-ok" onClick={dismissAlert}>OK</button>
                </div>
              </div>
            </div>,
            document.body
          )}
          {confirmState && createPortal(
            <div className="global-alert-overlay" onClick={() => handleConfirm(false)}>
              <div className="global-alert-modal" onClick={e => e.stopPropagation()}>
                <p className="global-alert-message">{confirmState.message}</p>
                <div className="global-alert-footer">
                  <button className="global-alert-ok global-confirm-cancel" onClick={() => handleConfirm(false)}>Cancel</button>
                  <button className="global-alert-ok global-confirm-ok" onClick={() => handleConfirm(true)}>OK</button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </ConfirmContext.Provider>
      </AlertContext.Provider>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};

export const useAlert = () => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used within ToastProvider');
  return ctx;
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ToastProvider');
  return ctx;
};
